import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = () => ({
  name: "",
  email: "",
  organization: "",
  message: "",
});

const ContactForm = ({ idAttr = "contact-form" }) => {
  const [form, setForm] = useState(initial());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please complete the required fields.", { description: "Name and email are required." });
      return;
    }
    setSubmitting(true);
    try {
      // API accepts `service` as the practice value + `phone` (empty here)
      const payload = {
        name: form.name,
        email: form.email,
        organization: form.organization,
        phone: "",
        service: "Relocation Services",
        message: form.message,
      };
      const res = await axios.post(`${API}/contact`, payload, { timeout: 15000 });
      if (res.data?.success) {
        toast.success("Message received", {
          description: res.data.message || "We will get back to you shortly.",
        });
        setForm(initial());
        setDone(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        msg = detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
      } else if (err?.response?.status) {
        msg = `Server error ${err.response.status}. Please try again.`;
      } else if (err?.code === "ECONNABORTED") {
        msg = "Request timed out. Please try again.";
      } else if (err?.message) {
        msg = `Network error: ${err.message}`;
      } else {
        msg = "Submission failed. Please try again.";
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div id={idAttr} data-testid="contact-form-success" className="bg-white border border-gray-200 p-10 md:p-14">
        <div className="eyebrow eyebrow--gold mb-4">Thank you</div>
        <h3 className="font-serif text-3xl text-[#000000] mb-4">Your message has been received.</h3>
        <p className="text-gray-600 leading-relaxed max-w-prose">
          A member of the Kassim Consulting team will be in touch shortly to schedule a call. In the meantime,
          feel free to explore our Projects page.
        </p>
        <button type="button" className="btn btn--secondary mt-8" data-testid="contact-form-reset" onClick={() => setDone(false)}>
          Send Another Message <span className="btn-arrow">→</span>
        </button>
      </div>
    );
  }

  return (
    <form id={idAttr} data-testid="contact-form" onSubmit={onSubmit} className="bg-white border border-gray-200 p-8 md:p-12" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
        <div>
          <label className="kc-label" htmlFor="cf-name">Name<span className="text-[#C5A880]"> *</span></label>
          <input id="cf-name" data-testid="contact-input-name" className="kc-input" type="text" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your full name" required />
        </div>
        <div>
          <label className="kc-label" htmlFor="cf-email">Email<span className="text-[#C5A880]"> *</span></label>
          <input id="cf-email" data-testid="contact-input-email" className="kc-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@organization.org" required />
        </div>
        <div className="md:col-span-2">
          <label className="kc-label" htmlFor="cf-org">Organization</label>
          <input id="cf-org" data-testid="contact-input-organization" className="kc-input" type="text" value={form.organization} onChange={(e) => update("organization", e.target.value)} placeholder="Company, agency, or nonprofit" />
        </div>
        <div className="md:col-span-2">
          <label className="kc-label" htmlFor="cf-message">Message <span className="text-gray-400 normal-case tracking-normal text-[11px] ml-1">(optional)</span></label>
          <textarea id="cf-message" data-testid="contact-input-message" className="kc-input resize-none" rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your project, timeline, and the outcome you are seeking." />
        </div>
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <p className="text-xs text-gray-500 max-w-sm">
          By submitting, you agree to be contacted by Kassim Consulting regarding your inquiry. We do not share your information with third parties.
        </p>
        <button type="submit" disabled={submitting} data-testid="contact-submit-button" className="btn btn--primary disabled:opacity-60 disabled:cursor-not-allowed">
          {submitting ? "Sending…" : "Contact Us"}
          <span className="btn-arrow">→</span>
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
